import { useState, useEffect } from "react";
import { toast } from "sonner";
import { MapPin, Phone, Clock, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ImageSlot } from "@/components/ImageSlot";
import { Seo } from "@/components/Seo";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function Contact() {
  const [s, setS] = useState({});
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "", cta_type: "General Inquiry" });
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => { api.get("/settings").then((r) => setS(r.data || {})).catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || (!form.email && !form.phone)) { toast.error("Please enter your name and a phone or email."); return; }
    setSubmitting(true);
    try {
      await api.post("/leads", { ...form, originating_page: "/contact" });
      toast.success("Thanks! We'll be in touch shortly.");
      setForm({ name: "", phone: "", email: "", message: "", cta_type: "General Inquiry" });
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  return (
    <div data-testid="contact-page">
      <Seo title="Contact MTM Marine | Bristol, TN" description="Contact MTM Marine Sales & Service in Bristol, TN. Call 423-878-3520 or send us a message about sales, service, or financing." />
      <section className="bg-mtm-charcoal text-white py-14">
        <div className="max-w-7xl mx-auto px-4">
          <p className="text-xs font-bold uppercase tracking-widest text-mtm-teal-bright font-mono">Get In Touch</p>
          <h1 className="text-4xl sm:text-5xl font-extrabold font-heading tracking-tight mt-2">Contact MTM Marine</h1>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 py-14 grid grid-cols-1 lg:grid-cols-2 gap-10">
        <div>
          <div className="space-y-5 text-slate-700">
            <p className="flex items-start gap-3"><MapPin size={20} className="text-mtm-teal shrink-0 mt-0.5" /> <span>{s.address_line || "3525 US-421 Scenic"}<br />{s.city_state_zip || "Bristol, TN 37620"}</span></p>
            <a href={`tel:${(s.phone || "4238783520").replace(/[^0-9]/g, "")}`} className="flex items-center gap-3 hover:text-mtm-purple"><Phone size={20} className="text-mtm-teal" /> {s.phone || "423-878-3520"}</a>
            {s.email && <a href={`mailto:${s.email}`} className="flex items-center gap-3 hover:text-mtm-purple"><Mail size={20} className="text-mtm-teal" /> {s.email}</a>}
            <div className="flex items-start gap-3"><Clock size={20} className="text-mtm-teal shrink-0 mt-0.5" /><div>{(s.hours || []).map((h, i) => <div key={i}>{h.day}: {h.value}</div>)}</div></div>
          </div>
          <div className="mt-6 rounded-lg overflow-hidden border border-slate-200">
            <iframe
              title="MTM Marine location map"
              src="https://www.google.com/maps?q=3525%20US-421%20Scenic%20Bristol%20TN%2037620&output=embed"
              className="w-full h-64 sm:h-80"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              data-testid="contact-map"
            />
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
          <h2 className="font-heading font-bold text-xl text-slate-900 mb-4">Send Us a Message</h2>
          <form onSubmit={submit} className="space-y-3">
            <div><Label htmlFor="c-name">Name *</Label><Input id="c-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label htmlFor="c-phone">Phone</Label><Input id="c-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="contact-phone" /></div>
              <div><Label htmlFor="c-email">Email</Label><Input id="c-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email" /></div>
            </div>
            <div>
              <Label>Reason</Label>
              <Select value={form.cta_type} onValueChange={(v) => setForm({ ...form, cta_type: v })}>
                <SelectTrigger data-testid="contact-reason"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="General Inquiry">General Inquiry</SelectItem>
                  <SelectItem value="Sales Inquiry">Sales Inquiry</SelectItem>
                  <SelectItem value="Service Inquiry">Service Inquiry</SelectItem>
                  <SelectItem value="Financing Information">Financing</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label htmlFor="c-msg">Message</Label><Textarea id="c-msg" rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message" /></div>
            <Button type="submit" disabled={submitting} className="w-full bg-mtm-purple hover:bg-mtm-purple-deep text-white rounded-full" data-testid="contact-submit">{submitting ? "Sending..." : "Send Message"}</Button>
          </form>
        </div>
      </section>
    </div>
  );
}
